import {
	Card,
	FONT_SIZE,
	ImgTile,
	PaddedContainer,
	SPACING,
	Text,
	isArray,
} from "@disco/disco_core";
import React from "react";

import "./lead-gen-brand.css";
import "./lead-gen-product.css";
import LeadGenProduct from "./LeadGenProduct";
import { BRAND_CLICK_TARGET } from "../../event_conf";
import useDimensions from "../../hooks/useDimensions";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import getImgUrl from "../../utils/getImgUrl";
import accessibilityProvider from "../../utils/accessibilityProvider";
import LeadGenAction from "./LeadGenAction";
import usePing, { PING_TYPES } from "../../hooks/usePing";
import getParsedRedirectUrl from "../../utils/getParsedRedirectUrl";
import linkProvider from "../../utils/linkProvider";
import { LeadGenV0BrandHeader } from "./LeadGenV0Brand";

const LeadGenV1Brand = ({
	brand,
	idx = -1,
	MAX_BRAND_LOGO_IMG_SIZE,
	defaultEventProps,
	isBrandActive = false,
	brandContainerRef,
	claimOffer,
	claimedOffers,
	isLastBrand = false,
	...rest
}) => {
	const {
		name: brandName,
		products,
		id: brandId,
		single_liner: brandDescription,
	} = brand || {};

	const track = useTrack();

	const brandRedirectUrl = getParsedRedirectUrl({ brand });

	const handleBrandNameClick = () => {
		track(eventTypes.CLICK_BRAND, {
			...defaultEventProps,
			initiatingTarget: BRAND_CLICK_TARGET.HEADER,
		});
	};

	const handleBrandLogoClick = () => {
		track(eventTypes.CLICK_BRAND, {
			...defaultEventProps,
			initiatingTarget: BRAND_CLICK_TARGET.LOGO,
		});
	};

	const handleExploreShopClick = () => {
		track(eventTypes.CLICK_BRAND, {
			...defaultEventProps,
			initiatingTarget: BRAND_CLICK_TARGET.SHOP_BUTTON,
		});
	};

	const parsedProducts = isArray(products)
		? products.slice(0, Math.min(2, products.length))
		: [];

	const isOfferClaimed =
		Array.isArray(claimedOffers) && claimedOffers.indexOf(brandId) !== -1;

	const { isLargeWidget } = useDimensions();

	const { ping: scrollToBrandIdx } = usePing({
		key: PING_TYPES.LEAD_GEN_CAROUSEL_JUMP_IDX,
	});
	const handleClick = () => {
		if (isBrandActive) return;
		scrollToBrandIdx(`${idx}`); // For truthy data if idx == 0
	};

	const handleOnOfferClaimed = () => {
		claimOffer();
	};

	const handleOnOfferClaimRedirect = () => {
		if (isLastBrand) return;
		if (
			document?.activeElement &&
			typeof document?.activeElement?.blur === "function"
		) {
			document.activeElement?.blur();
		}
		scrollToBrandIdx(idx + 1);
	};

	return (
		<PaddedContainer
			ref={brandContainerRef}
			{...rest}
			clickable={!isBrandActive}
			onClick={handleClick}
			data-intersectionuid={brandId}
			{...accessibilityProvider({
				name: `${brandName}'s offer`,
			})}
			onFocus={handleClick}
		>
			<Card hPadding={SPACING.REGULAR} vPadding={SPACING.REGULAR}>
				<PaddedContainer
					style={!isBrandActive ? { pointerEvents: "none" } : ""}
				>
					<LeadGenV0BrandHeader
						brand={brand}
						defaultEventProps={defaultEventProps}
					/>
					{/* TODO: Configured with different brand logo size, double check before turning back on */}
					<PaddedContainer marginTop={SPACING.SMALL}>
						<PaddedContainer
							flexContent
							onClick={handleExploreShopClick}
							{...linkProvider({ href: brandRedirectUrl })}
						>
							<Text
								size={
									isLargeWidget
										? FONT_SIZE.LG_BODY
										: FONT_SIZE.BODY
								}
							>
								Explore this shop
							</Text>
							<Text
								marginLeft={"auto"}
								color={"--disco-theme-mid"}
								size={
									isLargeWidget
										? FONT_SIZE.BODY
										: FONT_SIZE.LABEL
								}
							>
								See all
							</Text>
						</PaddedContainer>
						<PaddedContainer
							marginTop={SPACING.TINY}
							flexContent
							className="lead-gen-brand-products-container"
						>
							{parsedProducts.map((product, productIdx) => (
								<LeadGenProduct
									brand={brand}
									brandIdx={idx}
									key={productIdx}
									showPrices
									idx={productIdx}
									isParentInView={isBrandActive}
									product={product}
								/>
							))}
						</PaddedContainer>
					</PaddedContainer>
					<LeadGenAction
						brand={brand}
						marginTop={SPACING.REGULAR}
						isOfferClaimed={isOfferClaimed}
						onOfferClaimed={handleOnOfferClaimed}
						onRedirect={handleOnOfferClaimRedirect}
						defaultEventProps={defaultEventProps}
					/>
				</PaddedContainer>
			</Card>
		</PaddedContainer>
	);
};

export default LeadGenV1Brand;
